import { memo, useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useUnit } from 'effector-react'
import styled from 'styled-components'

import { API } from '../../components/API'
import { haptic } from '../../components/functions/haptic'
import { clearUserItems, getUserItems } from '../../components/store/collectedItems'
import { setScanQrFn } from '../../components/store/scanQrFn'
import { $initialScanQr, $userInfoBd, setInitialScanQr } from '../../components/store/user'
import { IAchivementResult } from '../../components/types/achievements'
import { Item, ILevelUpResult } from '../../components/types/Item'
import { IScanQr } from '../../components/types/user'
import { MAIN_COLORS } from '../consts/colors'
import {
  createQrResultShareModel,
  createQrResultSharePng,
  shareQrResultPng,
} from '../utils/createQrResultSharePng'
import Loader from './UIKit/Loader'
import MainButton from './UIKit/MainButton'
import Modal from './UIKit/Modal'
import RarityReveal from './RarityReveal'
import ToyResultCard from './ToyResultCard'

const DUPLICATE_KEYWORDS = ['already', 'уже', 'duplicate', 'дублик', 'существует']

// Коллекции собственных артов Лутян: цифровой дроп — отдельная история
// от фигурки в капсуле. Хардкод остаётся до появления признака на бэкенде.
const LUTYAN_COLLECTION_IDS = [
  '7d142627-4c68-43d5-a210-6b178666b421',
  '7173ceee-e78e-4ea9-8bdd-58258ada1dca',
]

const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    if (!window.matchMedia) return undefined
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = (event: MediaQueryListEvent | MediaQueryList) => setReduced(event.matches)
    update(query)

    if (query.addEventListener) {
      query.addEventListener('change', update)
      return () => query.removeEventListener('change', update)
    }

    query.addListener(update)
    return () => query.removeListener(update)
  }, [])

  return reduced
}

const isMilestoneCardRequiredError = (error: any): boolean => {
  const data = error.response?.data || {}
  const message = (data.message || '').toLowerCase()
  return error.response?.status === 409
    && data.is_milestone === true
    && message.includes('карт')
}

const isDuplicateError = (error: any): boolean => {
  const status = error.response?.status
  const message = (error.response?.data?.message || '').toLowerCase()
  if (status === 409 && !isMilestoneCardRequiredError(error)) return true
  return DUPLICATE_KEYWORDS.some(keyword => message.includes(keyword))
}

const ResultFlow = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

const RevealViewport = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  overflow: hidden;
  background: ${MAIN_COLORS.purple_100};
`

const ErrorModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px 16px;
  text-align: center;

  h2 { color: ${MAIN_COLORS.pink}; }
  h4 { color: ${MAIN_COLORS.purple_600}; }
`

const AchievementResult = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
`

const AchievementCard = styled.div`
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  padding: 20px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  text-align: center;
  background: ${MAIN_COLORS.gradient_purple_200};

  h1 { color: ${MAIN_COLORS.yellow}; }
  h2 {
    color: ${MAIN_COLORS.pink};
    text-transform: uppercase;
  }
  h4 { color: ${MAIN_COLORS.purple_900}; }
`

const AchievementImage = styled.img`
  width: min(64vw, 260px);
  height: min(40vh, 340px);
  object-fit: contain;
  filter: drop-shadow(0 0 18px ${MAIN_COLORS.pink}66);
`

const BonusBadge = styled.div`
  min-width: min(100%, 260px);
  padding: 10px 16px;
  border: 1px solid ${MAIN_COLORS.yellow}55;
  border-radius: 10px;
  color: ${MAIN_COLORS.yellow};
  background: ${MAIN_COLORS.gradient_purple_100};
  font-size: 20px;
  font-weight: 700;
`

const ProgressTrack = styled.div`
  width: min(100%, 320px);
  height: 8px;
  overflow: hidden;
  border-radius: 4px;
  background: ${MAIN_COLORS.purple_200};
`

const ProgressFill = styled.div<{ $percent: number }>`
  width: ${p => Math.min(100, Math.max(0, p.$percent))}%;
  height: 100%;
  border-radius: inherit;
  background: ${MAIN_COLORS.gradient_yellow_pink};
`

const SharePreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, .78);
`

const SharePreviewPanel = styled.div`
  width: min(100%, 420px);
  max-height: 92dvh;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  background: ${MAIN_COLORS.gradient_purple_200};
  text-align: center;

  h3 { color: ${MAIN_COLORS.yellow}; }
  p { color: ${MAIN_COLORS.purple_900}; }

  img {
    width: 100%;
    min-height: 0;
    max-height: 62dvh;
    object-fit: contain;
    border-radius: 10px;
  }
`

const DownloadLink = styled.a`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #fff;
  background: ${MAIN_COLORS.purple_500};
  text-decoration: none;
  font-size: 16px;
  font-weight: 700;
`

const refreshUserItems = async () => {
  clearUserItems()
  await getUserItems()
}

const makeShareFileName = (name: string) => {
  const safeName = name
    .toLowerCase()
    .replace(/[^a-zа-яё0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
  return `lootball-${safeName || 'result'}.png`
}

const QrResult = () => {
  const user = useUnit($userInfoBd)
  const initialScanQr = useUnit($initialScanQr)
  const reducedMotion = useReducedMotion()

  const [isLoading, setIsLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [errorModalVisible, setErrorModalVisible] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [qrImage, setQrImage] = useState('')
  const [qrData, setQrData] = useState<Item | IAchivementResult | null>(null)
  const [levelUpData, setLevelUpData] = useState<ILevelUpResult | null>(null)
  const [revealComplete, setRevealComplete] = useState(false)
  const [finalImageReady, setFinalImageReady] = useState(false)
  const [sharePng, setSharePng] = useState<Blob | null>(null)
  const [sharePreparing, setSharePreparing] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [sharePreviewUrl, setSharePreviewUrl] = useState<string | null>(null)

  const toyData = qrData && !('ach' in qrData) ? qrData : null
  const achievementData = qrData && 'ach' in qrData ? qrData : null
  const isLutyanToy = Boolean(
    toyData && LUTYAN_COLLECTION_IDS.includes(String(toyData.collection_id)),
  )

  const resetShare = useCallback(() => {
    setSharePng(null)
    setSharePreparing(false)
    setIsSharing(false)
    setSharePreviewUrl(null)
  }, [])

  const closeModal = useCallback(() => {
    setModalVisible(false)
    setQrImage('')
    setQrData(null)
    setLevelUpData(null)
    setRevealComplete(false)
    setFinalImageReady(false)
    resetShare()
  }, [resetShare])

  const closeErrorModal = useCallback(() => {
    setErrorModalVisible(false)
    setErrorMessage('')
  }, [])

  const showError = useCallback((message: string, feedback: 'error' | 'warning' = 'error') => {
    setIsLoading(false)
    haptic(feedback)
    setErrorMessage(message)
    setErrorModalVisible(true)
  }, [])

  const handleError = useCallback(async (error: any) => {
    showError(error.response?.data?.message || 'Произошла неизвестная ошибка')
  }, [showError])

  const handleToyScan = useCallback(async (data: IScanQr) => {
    try {
      const result: ILevelUpResult = await API.toys.addToCollection(data.value)
      await refreshUserItems()
      const toy = await API.toys.getSingle(result.toy_id)
      const image = await API.getImageData(toy.img_id, 'toys', 'unmasked')

      setQrData(toy)
      setQrImage(image)
      setLevelUpData(result)
      setRevealComplete(reducedMotion)
      setFinalImageReady(false)
      resetShare()
      setIsLoading(false)
      setModalVisible(true)
    } catch (error) {
      if (isMilestoneCardRequiredError(error)) {
        closeModal()
        showError(
          (error as any).response?.data?.message
            || 'Привяжите карту, чтобы получить бонус за новый уровень',
          'warning',
        )
        return
      }

      await refreshUserItems()
      if (isDuplicateError(error)) {
        closeModal()
        showError('Этот лутбол уже есть в вашей коллекции', 'warning')
        return
      }
      await handleError(error)
    }
  }, [closeModal, handleError, reducedMotion, resetShare, showError])

  const handleAchievementScan = useCallback(async (data: IScanQr) => {
    try {
      const response = await API.achievements.addToCollection(data.value)
      const achievement = response.achievement.achievement_details
      const image = await API.getImageData(achievement.id, 'achievements', 'unmasked')

      setQrData({ bonus_added: achievement.points, ach: achievement })
      setQrImage(image)
      setLevelUpData(null)
      setRevealComplete(true)
      setFinalImageReady(false)
      resetShare()
      setIsLoading(false)
      setModalVisible(true)
      haptic(achievement.bonus_awarded ? 'heavy' : 'success')
    } catch (error) {
      if (isDuplicateError(error)) {
        showError('Это достижение уже было получено ранее', 'warning')
        return
      }
      await handleError(error)
    }
  }, [handleError, resetShare, showError])

  const resolveShortUrl = useCallback(async (shortUrl: string) => {
    window.location.href = shortUrl
    window.setTimeout(() => window.Telegram?.WebApp?.close?.(), 2000)
  }, [])

  const scan = useCallback(async (input: IScanQr, type: 'initial' | 'notInitial') => {
    window.Telegram?.WebApp?.closeScanQrPopup?.()
    setIsLoading(true)

    if (!user) {
      setIsLoading(false)
      return
    }

    const data = { ...input }
    try {
      if (type === 'notInitial') {
        if (data.value.includes('clck.ru')) await resolveShortUrl(data.value)
        const startapp = new URL(data.value).searchParams.get('startapp')
        if (startapp) data.value = startapp.includes('achv')
          ? startapp.replace('achv', '').trim()
          : startapp
      }

      if (data.type === 'toy') await handleToyScan(data)
      else await handleAchievementScan(data)
    } catch (error) {
      await handleError(error)
    }
  }, [handleAchievementScan, handleError, handleToyScan, resolveShortUrl, user])

  useEffect(() => {
    setScanQrFn(scan)
  }, [scan])

  useEffect(() => {
    if (!initialScanQr) return
    scan(initialScanQr, 'initial')
    setInitialScanQr(null)
  }, [initialScanQr, scan])

  useEffect(() => {
    if (!modalVisible || !revealComplete || !finalImageReady
      || !toyData || !levelUpData || !qrImage) return undefined

    let cancelled = false
    setSharePreparing(true)
    setSharePng(null)
    const model = createQrResultShareModel({
      kind: 'toy',
      name: toyData.name,
      imageDataUrl: qrImage,
      rarity: toyData.rare,
      level: levelUpData.level,
      isMilestone: levelUpData.is_milestone,
      isMaxLevel: levelUpData.is_max_level,
      bonusAdded: levelUpData.bonus_added,
      nextMilestoneLevel: levelUpData.next_milestone_level,
      isLutyan: isLutyanToy,
    })

    createQrResultSharePng(model)
      .then(blob => {
        if (!cancelled) setSharePng(blob)
      })
      .catch(() => {
        if (!cancelled) setSharePng(null)
      })
      .finally(() => {
        if (!cancelled) setSharePreparing(false)
      })

    return () => { cancelled = true }
  }, [
    finalImageReady,
    isLutyanToy,
    levelUpData,
    modalVisible,
    qrImage,
    revealComplete,
    toyData,
  ])

  useEffect(() => () => {
    if (sharePreviewUrl) URL.revokeObjectURL(sharePreviewUrl)
  }, [sharePreviewUrl])

  const openSharePreview = useCallback((png: Blob) => {
    setSharePreviewUrl(URL.createObjectURL(png))
  }, [])

  const handleShare = useCallback(async () => {
    if (!sharePng || !toyData || sharePreparing || isSharing) return
    setIsSharing(true)
    try {
      const outcome = await shareQrResultPng(sharePng, {
        fileName: makeShareFileName(toyData.name),
        title: `Мой LootBall — ${toyData.name}`,
        text: `${toyData.name}: ${levelUpData?.level || 1} из 5 звёзд`,
      })
      if (outcome === 'unsupported') openSharePreview(sharePng)
    } catch (error) {
      if ((error as DOMException)?.name !== 'AbortError') openSharePreview(sharePng)
    } finally {
      setIsSharing(false)
    }
  }, [isSharing, levelUpData?.level, openSharePreview, sharePng, sharePreparing, toyData])

  const achievementProgress = achievementData
    ? Math.min(100, Math.max(0, achievementData.ach.threshold > 0
      ? achievementData.ach.progress / achievementData.ach.threshold * 100
      : 0))
    : 0

  return (
    <>
      {isLoading && <Loader />}

      {modalVisible && toyData && levelUpData && !revealComplete && createPortal(
        <RevealViewport>
          <RarityReveal
            rarity={toyData.rare}
            imageSrc={qrImage}
            reducedMotion={reducedMotion}
            onDone={() => setRevealComplete(true)}
          />
        </RevealViewport>,
        document.body,
      )}

      <Modal
        closable={false}
        fullscreen={false}
        isOpen={errorModalVisible}
        close={closeErrorModal}
      >
        <ErrorModalWrapper>
          <h2>Произошла ошибка</h2>
          <h4>{errorMessage}</h4>
          <MainButton onClick={closeErrorModal}>Закрыть</MainButton>
        </ErrorModalWrapper>
      </Modal>

      <Modal
        closable={false}
        fullscreen
        isOpen={modalVisible}
        close={closeModal}
      >
        <ResultFlow>
          {toyData && levelUpData && revealComplete && (
            <ToyResultCard
              name={toyData.name}
              imageSrc={qrImage}
              rarity={toyData.rare}
              levelUpData={levelUpData}
              isLutyan={isLutyanToy}
              reducedMotion={reducedMotion}
              onImageReady={() => setFinalImageReady(true)}
              onContinue={closeModal}
              onShare={handleShare}
              shareDisabled={!sharePng || sharePreparing || isSharing}
              isSharing={sharePreparing || isSharing}
            />
          )}

          {achievementData && (
            <AchievementResult>
              <AchievementCard>
                <h2>{achievementData.ach.bonus_awarded ? 'Бонусный уровень' : 'Достижение открыто'}</h2>
                <AchievementImage src={qrImage} alt={achievementData.ach.name} />
                <h1>{achievementData.ach.name}</h1>
                <h4>{achievementData.ach.description}</h4>
                <BonusBadge>+ {achievementData.bonus_added} Бонусов</BonusBadge>
                {achievementData.ach.bonus_points > 0 && (
                  <BonusBadge>+ {achievementData.ach.bonus_points} Бонусов за достижение</BonusBadge>
                )}
                {achievementData.ach.threshold > 0 && (
                  <>
                    <ProgressTrack>
                      <ProgressFill $percent={achievementProgress} />
                    </ProgressTrack>
                    <h4>
                      {achievementData.ach.progress >= achievementData.ach.threshold
                        ? 'Выполнено!'
                        : `${achievementData.ach.progress} из ${achievementData.ach.threshold}`}
                    </h4>
                  </>
                )}
              </AchievementCard>
              <MainButton onClick={closeModal}>Продолжить</MainButton>
            </AchievementResult>
          )}
        </ResultFlow>
      </Modal>

      {sharePreviewUrl && toyData && (
        <SharePreviewOverlay role="dialog" aria-modal="true" aria-label="PNG результата">
          <SharePreviewPanel>
            <h3>PNG готов</h3>
            <p>На этом устройстве системный шеринг файлов недоступен. Сохраните картинку вручную.</p>
            <img src={sharePreviewUrl} alt={`Результат: ${toyData.name}`} />
            <DownloadLink href={sharePreviewUrl} download={makeShareFileName(toyData.name)}>
              Сохранить PNG
            </DownloadLink>
            <MainButton onClick={() => setSharePreviewUrl(null)}>Закрыть</MainButton>
          </SharePreviewPanel>
        </SharePreviewOverlay>
      )}
    </>
  )
}

export default memo(QrResult)
